import api_base, { serializeParams } from './base'
import type { 
  DocumentTemplate, 
  DocumentTemplateList, 
  DocumentTemplateListParams, 
  RenderDocumentResponse, 
  TemplateVariable, 
  TemplateVariableCreate,
  PaginatedTemplateVariables,
  TemplateVariableListParams
} from '@/types/document-template'

export const list_document_templates = async (
  params: DocumentTemplateListParams = {}
): Promise<DocumentTemplateList> => {
  const query = serializeParams({
    q: params.q ?? null,
    page_size: params.page_size ?? 12,
    page: params.page ?? 0,
  })
  const { data } = await api_base.get(`/api/v1/document-templates?${query}`)
  return data
}

export const get_document_template = async (slug: string): Promise<DocumentTemplate> => {
  const { data } = await api_base.get(`/api/v1/document-templates/${slug}`)
  return data
}

export const get_page_layouts = async (params: { page?: number; search?: string } = {}): Promise<any> => {
  const query = serializeParams({
    page: params.page ?? null,
    search: params.search ?? null,
  })
  const { data } = await api_base.get(`/api/v1/super-admin/page-layouts/?${query}`)
  return data
}

export const list_template_variables = async (
  params: TemplateVariableListParams = {}
): Promise<PaginatedTemplateVariables> => {
  const query = serializeParams({
    search: params.search && params.search.trim() ? params.search.trim() : null,
    source: params.source ?? null,
    variable_type: params.variable_type ?? null,
    is_active: params.is_active !== null ? params.is_active : null,
    ordering: params.ordering ?? null,
    page_size: params.page_size ?? 20,
    page: params.page ?? 1,
  })
  const { data } = await api_base.get(`/api/v1/template-variables/?${query}`)
  return data
}

export const get_available_variable_keys = async (): Promise<TemplateVariable[]> => {
  const { data } = await api_base.get('/api/v1/template-variables/available/')
  return data
}

export const create_template_variable = async (variable: TemplateVariableCreate): Promise<TemplateVariable> => {
  const { data } = await api_base.post('/api/v1/template-variables/', variable)
  return data
}

export const render_document_template = async (
  slug: string,
  payload: Record<string, unknown>
): Promise<RenderDocumentResponse> => {
  const { data } = await api_base.post(`/api/v1/document-templates/${slug}/render`, { payload })
  return data
}

export const validate_word_template = async (file: File): Promise<{
  valid: boolean
  variables: string[]
  valid_variables?: string[]
  invalid_variables?: string[]
  message?: string
  errors?: string[]
}> => {
  const formData = new FormData()
  formData.append('file', file)
  
  try {
    const { data } = await api_base.post('/api/v1/super-admin/document-templates/validate-word/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      validateStatus: () => true, // Accept all status codes (200, 400, etc.)
    })
    return data
  } catch (error: any) {
    // If error response has data, return it as validation result
    if (error?.response?.data && typeof error.response.data === 'object' && 'valid' in error.response.data) {
      return error.response.data
    }
    throw error
  }
}

export const create_document_template = async (templateData: {
  slug: string
  title: string
  engine: 'html' | 'word'
  html_body?: string
  category?: string | null
  page_layout?: string | null
  file?: File
}): Promise<DocumentTemplate> => {
  const formData = new FormData()
  formData.append('slug', templateData.slug)
  formData.append('title', templateData.title)
  formData.append('engine', templateData.engine)
  formData.append('is_active', 'true')
  
  if (templateData.html_body) {
    formData.append('html_body', templateData.html_body)
  }
  
  if (templateData.category) {
    formData.append('category', templateData.category)
  }
  
  if (templateData.page_layout) {
    formData.append('page_layout', templateData.page_layout)
  }
  
  if (templateData.file) {
    // Backend expects 'word_file' field name for Word documents
    formData.append('word_file', templateData.file)
  }
  
  const { data } = await api_base.post('/api/v1/super-admin/document-templates/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return data
}

export const update_document_template = async (
  slug: string,
  templateData: {
    title?: string
    is_active?: boolean
    engine?: 'html' | 'word'
    html_body?: string
    category?: string | null
    page_layout?: string | null
  }
): Promise<DocumentTemplate> => {
  const { data } = await api_base.patch(`/api/v1/super-admin/document-templates/${slug}/`, templateData)
  return data
}

export const delete_document_template = async (slug: string): Promise<void> => {
  await api_base.delete(`/api/v1/super-admin/document-templates/${slug}/`)
}

export const update_template_variable = async (
  id: string,
  variable: Partial<TemplateVariableCreate>
): Promise<TemplateVariable> => {
  const { data } = await api_base.patch(`/api/v1/template-variables/${id}/`, variable)
  return data
}

export const delete_template_variable = async (id: string): Promise<void> => {
  await api_base.delete(`/api/v1/template-variables/${id}/`)
}